// Gallery.jsx — the shared pool of pictures.
//
// ── A GRID, NOT A TABLE ──
// The screen this replaces listed photographs in a table: a 90px thumbnail
// column, then the filename, then Created By and Created Date. Four columns of
// metadata beside a picture too small to judge. You pick a photograph by
// looking at it, so the picture is the row now and the filename is a caption
// under it.
//
// ── TWO SHAPES OF SAVE ──
// Adding takes many files at once and gives them one caption; editing takes
// one row and optionally replaces its file. They are different enough that the
// modal says which it is doing rather than trying to be both at once.
//
// ── THE UPLOAD IS A SEPARATE STEP ──
// /admin/content/gallery takes relative uploads paths, never files. Pictures
// go up through /admin/media/gallery first and the paths travel in the JSON.
// On an edit, leaving `url` out of the body keeps the stored picture.

import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { uploadImage, uploadImages } from "../../../api/media";
import {
  fetchGalleryImages,
  bulkUploadImages,
  updateGalleryImage,
  deleteGalleryImage,
} from "../../../slice/gallery/galleryImageSlice";
import {
  PageShell,
  Button,
  RowActions,
  Modal,
  Field,
  ImagePicker,
  Pager,
  PlusIcon,
} from "../component/AdminUi";

const BASE_URL = process.env.REACT_APP_API_URL;
const PER_PAGE = 12;

const pictureOf = (row) => {
  const raw = (row?.url || row?.imageUrl || row?.image || "").trim();
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `${BASE_URL}/uploads/${raw.replace(/^\/+/, "").replace(/^uploads\//, "")}`;
};

const fileNameOf = (row) => {
  const raw = (row?.url || row?.imageUrl || row?.image || "").trim();
  return raw ? raw.split("/").pop() : "";
};

export default function Gallery() {
  const dispatch = useDispatch();
  const { images, loading, error, pagination } = useSelector(
    (s) => s.galleryImage
  );

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  /* Adding: many files at once. Editing: at most one replacement. */
  const [picked, setPicked] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [swapFile, setSwapFile] = useState(null);
  const [swapPreview, setSwapPreview] = useState(null);
  const filesInput = useRef(null);

  useEffect(() => {
    dispatch(fetchGalleryImages({ page, limit: PER_PAGE, search: query }));
  }, [dispatch, page, query]);

  /* Object URLs are released when the picks change, or they leak for as long
     as the tab is open. */
  useEffect(() => {
    return () => previews.forEach((u) => URL.revokeObjectURL(u));
  }, [previews]);

  const reload = () =>
    dispatch(fetchGalleryImages({ page, limit: PER_PAGE, search: query }));

  /* ── picking ───────────────────────────────────────────────────────────── */

  const takeFiles = (list) => {
    const files = Array.from(list || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (!files.length) return;
    setPicked((p) => [...p, ...files]);
    setPreviews((p) => [...p, ...files.map((f) => URL.createObjectURL(f))]);
    setProblem("");
  };

  const dropPick = (at) => {
    URL.revokeObjectURL(previews[at]);
    setPicked((p) => p.filter((_, i) => i !== at));
    setPreviews((p) => p.filter((_, i) => i !== at));
  };

  /* ── opening and closing ───────────────────────────────────────────────── */

  const openNew = () => {
    setEditing(null);
    setCaption("");
    setPicked([]);
    setPreviews([]);
    setSwapFile(null);
    setSwapPreview(null);
    setProblem("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setCaption(row.caption || "");
    setPicked([]);
    setPreviews([]);
    setSwapFile(null);
    setSwapPreview(pictureOf(row) || null);
    setProblem("");
    setOpen(true);
  };

  const close = () => {
    previews.forEach((u) => URL.revokeObjectURL(u));
    setOpen(false);
    setEditing(null);
    setCaption("");
    setPicked([]);
    setPreviews([]);
    setSwapFile(null);
    setSwapPreview(null);
    setProblem("");
  };

  /* ── saving ────────────────────────────────────────────────────────────── */

  const save = async () => {
    setProblem("");

    if (editing) {
      setSaving(true);
      const body = { caption: caption.trim() };
      try {
        if (swapFile) body.url = await uploadImage(swapFile, "gallery");
        await dispatch(
          updateGalleryImage({ id: editing.id, payload: body })
        ).unwrap();
        toast.success("Picture updated");
        reload();
        close();
      } catch (err) {
        const message = err || "That did not save.";
        setProblem(message);
        toast.error(message);
      } finally {
        setSaving(false);
      }
      return;
    }

    if (picked.length === 0) return setProblem("Choose at least one picture.");

    setSaving(true);
    try {
      /* Uploaded in the order they were picked, so the paths come back in
         the same order the person chose them in. */
      const urls = await uploadImages(picked, "gallery");
      const res = await dispatch(
        bulkUploadImages({ caption: caption.trim(), urls })
      ).unwrap();
      toast.success(res?.message || `${urls.length} picture(s) added`);
      setPage(1);
      dispatch(fetchGalleryImages({ page: 1, limit: PER_PAGE, search: query }));
      close();
    } catch (err) {
      const message = err || "That did not upload.";
      setProblem(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this picture? Anything using it loses it."))
      return;
    try {
      await dispatch(deleteGalleryImage(row.id)).unwrap();
      toast.success("Picture deleted");
      /* The screen this replaces deleted and never refetched, so the row sat
         on screen until something else happened to reload the list. */
      reload();
    } catch (err) {
      toast.error(err || "Could not delete it.");
    }
  };

  const rows = images || [];

  /* ── render ────────────────────────────────────────────────────────────── */

  return (
    <PageShell
      title="Gallery"
      count={
        loading && !rows.length
          ? "Loading…"
          : pagination?.total != null
          ? `${pagination.total} picture${pagination.total === 1 ? "" : "s"}`
          : `${rows.length} shown`
      }
      search={{
        value: query,
        onChange: (v) => {
          setQuery(v);
          setPage(1);
        },
        placeholder: "Search captions",
      }}
      actions={
        <Button kind="primary" onClick={openNew}>
          <PlusIcon />
          Add pictures
        </Button>
      }
    >
      {error ? <div className="ui-alert ui-alert--error">{error}</div> : null}

      {loading && !rows.length ? (
        <div className="ui-table-wrap">
          <div className="ui-loading" />
          <div className="ui-state">
            <span className="ui-state__title">Loading…</span>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="ui-table-wrap">
          <div className="ui-state">
            <span className="ui-state__title">
              {query ? "No picture matches that" : "No pictures yet"}
            </span>
            <span className="ui-state__hint">
              {query
                ? "Captions are what is searched."
                : "Add photographs here once and use them across the catalogue."}
            </span>
          </div>
        </div>
      ) : (
        <div className="ui-gallery">
          {rows.map((row) => (
            <figure className="ui-shot" key={row.id}>
              <img
                className="ui-shot__img"
                src={pictureOf(row)}
                alt={row.caption || fileNameOf(row)}
                loading="lazy"
              />
              <figcaption className="ui-shot__foot">
                <span style={{ minWidth: 0 }}>
                  <span className="ui-shot__caption">
                    {row.caption || <span className="ui-muted">No caption</span>}
                  </span>
                  <span className="ui-shot__file">{fileNameOf(row)}</span>
                </span>
                <RowActions
                  onEdit={() => openEdit(row)}
                  onDelete={() => remove(row)}
                />
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      <Pager
        page={page}
        totalPages={pagination?.totalPages}
        total={pagination?.total}
        perPage={PER_PAGE}
        onPage={setPage}
      />

      <Modal
        open={open}
        title={editing ? "Edit picture" : "Add pictures"}
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button kind="primary" onClick={save} disabled={saving}>
              {saving
                ? "Saving…"
                : editing
                ? "Save changes"
                : `Add ${picked.length || ""}`.trim()}
            </Button>
          </>
        }
      >
        {problem ? <div className="ui-alert ui-alert--error">{problem}</div> : null}

        {editing ? (
          <Field
            label="Picture"
            hint="Leave it alone to keep the one that is there."
          >
            <ImagePicker
              preview={swapPreview}
              onPick={(file) => {
                setSwapFile(file);
                setSwapPreview(URL.createObjectURL(file));
              }}
              hint="Pick a file to replace it"
            />
          </Field>
        ) : (
          <Field label="Pictures" required hint="You can choose several at once.">
            <div
              className="ui-drop"
              onClick={() => filesInput.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                takeFiles(e.dataTransfer.files);
              }}
            >
              <span className="ui-drop__title">
                Drop pictures, or click to choose
              </span>
              <span className="ui-drop__hint">PNG, JPG or WebP</span>
              <input
                ref={filesInput}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  takeFiles(e.target.files);
                  // Cleared so the same file can be chosen twice.
                  e.target.value = "";
                }}
              />
            </div>

            {previews.length > 0 ? (
              <div className="ui-picked">
                {previews.map((src, i) => (
                  <figure key={src}>
                    <img src={src} alt="" />
                    <button
                      type="button"
                      onClick={() => dropPick(i)}
                      aria-label="Remove this one"
                      title="Remove this one"
                    >
                      ×
                    </button>
                  </figure>
                ))}
              </div>
            ) : null}
          </Field>
        )}

        <Field
          label="Caption"
          hint={
            editing
              ? "What this picture is of."
              : "Applied to every picture in this batch."
          }
        >
          <input
            className="ui-input"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g. Turmeric, whole"
          />
        </Field>
      </Modal>
    </PageShell>
  );
}
